import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  AssignmentTurnedIn,
  DeleteOutline,
  Edit,
  InsertDriveFile,
  Message,
  Restore,
  Settings,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ChatbotModal from "../../components/ChatbotModal";
import MessageBubblePreview from "../../components/MessageBubblePreview";
import ModalImageCors from "../../components/ModalImageCors";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { buildWhatsappTabs } from "../../utils/connectionsTabs";
import {
  buildMessageVariables,
  fetchContactCustomFieldNames,
  fetchWasheetMessageVariables,
} from "../../utils/messageVariables";
import { getBackendUrl } from "../../config";

const ALL_CONNECTIONS = "all-process-connections";
const NO_CONNECTION = "no-process-connection";
const NO_SEQUENCE = "no-sequence";
const ALL_PROCESSES = "all-processes-filter";
const NO_PROCESS = "no-process-selected";
const NO_FIELD_SELECTION = "no-field-selection";
const DEFAULT_TEMPLATE_SLUGS = [
  "solicitud_de_credito",
  "solicitud_de_credito_con_opciones",
];
const acceptedMedia =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar,.webp";

const runStatuses = [
  { value: "in_progress", label: "En progreso" },
  { value: "ready_to_finish", label: "Listo para finalizar" },
  { value: "completed", label: "Completado" },
  { value: "cancelled", label: "Cancelado" },
];

const fieldTypes = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Numero" },
  { value: "date", label: "Fecha" },
  { value: "email", label: "Correo" },
  { value: "phone", label: "Telefono" },
  { value: "textarea", label: "Texto largo" },
  { value: "location", label: "Ubicacion" },
  { value: "select", label: "Desplegable" },
  { value: "multiple", label: "Multiple" },
];

const conditionOperators = [
  { value: "equals", label: "Es igual" },
  { value: "not_equals", label: "No es igual" },
  { value: "contains", label: "Contiene" },
];

const emptyConditionalConfig = () => ({
  enabled: false,
  sourceFieldKey: "",
  operator: "equals",
  value: "",
});

const emptyStage = () => ({
  name: "Nueva etapa",
  instructions: "",
  fields: [],
  fileRequirements: [],
  messageBlocks: [],
});

const emptyField = () => ({
  label: "Nuevo dato",
  key: "",
  type: "text",
  options: [],
  required: true,
  confirmValue: true,
  sendPrompt: true,
  helpText: "",
  conditionalConfig: emptyConditionalConfig(),
});

const emptyOption = () => "Nueva opcion";

const emptyFile = () => ({
  label: "Nuevo documento",
  key: "",
  required: true,
  helpText: "",
  conditionalConfig: emptyConditionalConfig(),
});

const emptyMessageBlock = () => ({
  text: "",
  delaySeconds: 0,
  file: null,
  media: null,
});

const emptyProcess = () => ({
  name: "",
  description: "",
  instructions: "",
  conclusionInstructions: "",
  isActive: true,
  allowMultipleRuns: false,
  sendConfirmation: true,
  finalSequenceId: NO_SEQUENCE,
  connectionId: NO_CONNECTION,
  sharedConnectionIds: [],
  stages: [emptyStage()],
});

const emptyRun = processId => ({
  id: null,
  processId: processId || NO_PROCESS,
  status: "in_progress",
  conclusion: "",
  contactId: "",
  ticketId: "",
  values: {},
  files: [],
});

const normalizeConnectionIds = (...values) => {
  const ids = new Set();
  values.forEach(value => {
    if (!value || value === NO_CONNECTION) return;
    if (Array.isArray(value)) {
      value.forEach(id => {
        const numberId = Number(id);
        if (numberId) ids.add(numberId);
      });
      return;
    }
    const numberId = Number(value);
    if (numberId) ids.add(numberId);
  });
  return Array.from(ids);
};

const getConnectionName = (connections, connectionId) =>
  connections.find(connection => Number(connection.id) === Number(connectionId))?.name ||
  connections.find(connection => Number(connection.id) === Number(connectionId))?.number ||
  `Conexion ${connectionId}`;

const formatSequenceOption = sequence => `ID: ${sequence.id} - ${sequence.name}`;

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    padding: theme.spacing(1.5),
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  table: {
    minWidth: 920,
  },
  processName: {
    fontWeight: 600,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  sheetConfigFields: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 140px",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sheetConfigStatus: {
    marginTop: theme.spacing(1),
    minHeight: 20,
  },
  stickyDialogTitle: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogTitleContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  dialogSwitches: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  headerSwitch: {
    marginRight: 0,
  },
  stageBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  stageHeader: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  orderActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  itemBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.25),
    marginTop: theme.spacing(1),
    marginBottom: 30,
  },
  itemHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  rowFields: {
    display: "grid",
    gridTemplateColumns: "1fr 150px 110px 190px 170px",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  fieldOptionRow: {
    display: "grid",
    gridTemplateColumns: "auto 72px 1fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  fileRow: {
    display: "grid",
    gridTemplateColumns: "1fr 100px",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageRow: {
    display: "grid",
    gridTemplateColumns: "330px minmax(0, 1fr) 90px",
    gap: theme.spacing(1),
    alignItems: "start",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "290px minmax(0, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageDelayField: {
    [theme.breakpoints.down("sm")]: {
      gridColumn: "2",
    },
    [theme.breakpoints.down("xs")]: {
      gridColumn: "auto",
    },
  },
  variablesBox: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    background: theme.palette.background.paper,
  },
  variablesLabel: {
    display: "block",
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  variablesChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  messageFileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  messageFileName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },
  helper: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  conditionalRow: {
    background: theme.palette.action.hover,
    borderRadius: 6,
    padding: theme.spacing(1.25),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  conditionalHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontWeight: 600,
  },
  conditionalFields: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  runFilesSection: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
  },
  runFileItem: {
    display: "grid",
    gridTemplateColumns: "1fr minmax(260px, 1fr)",
    gap: theme.spacing(1),
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  runFileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.25),
  },
  runFileActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(0.75),
    alignItems: "center",
  },
  runFilePreviewWrap: {
    marginTop: theme.spacing(0.75),
  },
  filePreview: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    height: 120,
    maxWidth: 180,
    objectFit: "cover",
    overflow: "hidden",
    width: "100%",
  },
  pdfPreview: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    height: 180,
    width: "100%",
  },
  sectionActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  chipWrap: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
  },
}));

const normalizeProcess = process => ({
  ...emptyProcess(),
  ...process,
  connectionId: process?.connectionId || NO_CONNECTION,
  allowMultipleRuns: process?.allowMultipleRuns === true,
  sendConfirmation: process?.sendConfirmation !== false,
  finalSequenceId: process?.finalSequenceId ? String(process.finalSequenceId) : NO_SEQUENCE,
  sharedConnectionIds: normalizeConnectionIds(
    process?.sharedConnectionIds,
    process?.connectionId
  ),
  stages: (process?.stages || []).map(stage => ({
    ...stage,
    fields: (stage.fields || []).map(field => ({
      ...field,
      confirmValue: field.confirmValue !== false,
      sendPrompt: field.sendPrompt !== false,
      options: Array.isArray(field.options)
        ? field.options
            .map(option =>
              option && typeof option === "object"
                ? String(option.label || "").trim()
                : String(option || "").trim()
            )
            .filter(Boolean)
        : [],
      conditionalConfig: {
        ...emptyConditionalConfig(),
        ...(field.conditionalConfig || {}),
      },
    })),
    fileRequirements: (stage.fileRequirements || []).map(file => ({
      ...file,
      conditionalConfig: {
        ...emptyConditionalConfig(),
        ...(file.conditionalConfig || {}),
      },
    })),
    messageBlocks: (stage.messageBlocks || []).map(block => ({
      ...block,
      text: block.text || "",
      delaySeconds: Number(block.delaySeconds || 0),
      file: null,
      media: normalizeMessageBlockMedia(block.media),
    })),
  })),
});

const normalizeMessageBlockMedia = media => {
  const parsedMedia =
    typeof media === "string"
      ? (() => {
          try {
            return JSON.parse(media);
          } catch {
            return null;
          }
        })()
      : media;

  if (!parsedMedia || typeof parsedMedia !== "object") return null;

  const filename = String(parsedMedia.filename || "").trim();
  const originalName = String(
    parsedMedia.originalName || parsedMedia.filename || ""
  ).trim();
  const mimetype = String(parsedMedia.mimetype || "").trim();

  if (!filename || !mimetype) return null;

  return {
    filename,
    originalName: originalName || filename,
    mimetype,
  };
};

const normalizeKey = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getUniqueKey = (value, usedKeys, fallback) => {
  const baseKey = normalizeKey(value) || fallback;
  let key = baseKey;
  let suffix = 2;

  while (usedKeys.has(key)) {
    key = `${baseKey}_${suffix}`;
    suffix += 1;
  }

  usedKeys.add(key);
  return key;
};

const optionsToText = options =>
  Array.isArray(options) ? options.join("\n") : String(options || "");

const textToOptions = value =>
  Array.from(
    new Set(
      String(value || "")
        .split("\n")
        .map(option => option.trim())
        .filter(Boolean)
    )
  );

const valueToMultipleOptions = value =>
  String(value || "")
    .split(",")
    .map(option => option.trim())
    .filter(Boolean);

const optionId = index => String(index + 1);

const optionLabelById = (options, id) => {
  const index = Number(id) - 1;
  return Array.isArray(options) && options[index] ? options[index] : id;
};

const selectedOptionLabels = (options, selected) =>
  selected.includes(NO_FIELD_SELECTION)
    ? "Sin seleccion"
    : selected.map(id => optionLabelById(options, id)).join(", ");

const moveArrayItem = (items, fromIndex, toIndex) => {
  if (toIndex < 0 || toIndex >= items.length) return items;
  const nextItems = [...items];
  const [item] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, item);
  return nextItems;
};

const getStageContentItems = stage => {
  const rawItems = [
    ...(stage.fields || []).map((item, index) => ({
      type: "fields",
      item,
      index,
      fallbackPosition: index,
    })),
    ...(stage.fileRequirements || []).map((item, index) => ({
      type: "fileRequirements",
      item,
      index,
      fallbackPosition: (stage.fields || []).length + index,
    })),
    ...(stage.messageBlocks || []).map((item, index) => ({
      type: "messageBlocks",
      item,
      index,
      fallbackPosition:
        (stage.fields || []).length + (stage.fileRequirements || []).length + index,
    })),
  ];
  const positions = rawItems.map(contentItem => Number(contentItem.item.position));
  const hasUniquePositions =
    positions.length > 0 &&
    positions.every(position => Number.isFinite(position)) &&
    new Set(positions).size === positions.length;

  return rawItems
    .map(contentItem => ({
      ...contentItem,
      position: hasUniquePositions
        ? Number(contentItem.item.position)
        : contentItem.fallbackPosition,
    }))
    .sort((a, b) => a.position - b.position || a.fallbackPosition - b.fallbackPosition);
};

const normalizeStageContentOrder = stage => {
  const normalizedStage = {
    ...stage,
    fields: [...(stage.fields || [])],
    fileRequirements: [...(stage.fileRequirements || [])],
    messageBlocks: [...(stage.messageBlocks || [])],
  };

  getStageContentItems(normalizedStage).forEach((contentItem, position) => {
    normalizedStage[contentItem.type][contentItem.index] = {
      ...contentItem.item,
      position,
    };
  });

  return normalizedStage;
};

const normalizeConditionalPayload = config => {
  const nextConfig = {
    ...emptyConditionalConfig(),
    ...(config || {}),
  };

  return {
    enabled: nextConfig.enabled === true,
    sourceFieldKey: normalizeKey(nextConfig.sourceFieldKey || ""),
    operator: conditionOperators.some(operator => operator.value === nextConfig.operator)
      ? nextConfig.operator
      : "equals",
    value: String(nextConfig.value || "").trim(),
  };
};

const Processes = () => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [processes, setProcesses] = useState([]);
  const [runs, setRuns] = useState([]);
  const [connections, setConnections] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [connectionFilter, setConnectionFilter] = useState(ALL_CONNECTIONS);
  const [processFilter, setProcessFilter] = useState(ALL_PROCESSES);
  const [modalOpen, setModalOpen] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [editingProcess, setEditingProcess] = useState(emptyProcess());
  const [editingRun, setEditingRun] = useState(emptyRun());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteRunTarget, setDeleteRunTarget] = useState(null);
  const [deleteRunFileTarget, setDeleteRunFileTarget] = useState(null);
  const [resetTemplateTarget, setResetTemplateTarget] = useState(null);
  const [statusToggleProcess, setStatusToggleProcess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingFileKey, setUploadingFileKey] = useState("");
  const [customFieldNames, setCustomFieldNames] = useState([]);
  const [washeetVariables, setWasheetVariables] = useState([]);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const [sheetModalOpen, setSheetModalOpen] = useState(false);
  const [sheetProcessId, setSheetProcessId] = useState(NO_PROCESS);
  const [sheetSpreadsheetId, setSheetSpreadsheetId] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetSaving, setSheetSaving] = useState(false);
  const [sheetSyncing, setSheetSyncing] = useState(false);
  const [sheetStatus, setSheetStatus] = useState("");
  const sequenceModalApplySelection = useRef(null);

  const whatsappTabs = useMemo(
    () => buildWhatsappTabs(connections),
    [connections]
  );
  const messageVariables = useMemo(
    () => buildMessageVariables(customFieldNames, washeetVariables),
    [customFieldNames, washeetVariables]
  );

  const loadConnections = async () => {
    try {
      const { data } = await api.get("/connections");
      setConnections(Array.isArray(data) ? data : data?.connections || []);
    } catch (err) {
      toastError(err);
    }
  };

  const loadSequences = async () => {
    try {
      const { data } = await api.get("/chatbot/sequences", {
        params: { all: true, pageNumber: 1, searchParam: "" },
      });
      setSequences(data.sequences || []);
    } catch (err) {
      toastError(err);
    }
  };

  const loadProcesses = async () => {
    try {
      setLoading(true);
      const params =
        connectionFilter === ALL_CONNECTIONS
          ? {}
          : { connectionId: Number(connectionFilter) };
      const { data } = await api.get("/ai-processes", { params });
      setProcesses(data.processes || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadRuns = async () => {
    try {
      const params = processFilter === ALL_PROCESSES ? {} : { processId: processFilter };
      const { data } = await api.get("/ai-processes/runs", { params });
      setRuns(data.runs || []);
    } catch (err) {
      toastError(err);
    }
  };

  const loadSheetConfig = async processId => {
    if (!processId || processId === NO_PROCESS) {
      setSheetSpreadsheetId("");
      setSheetName("");
      setSheetStatus("");
      return;
    }

    try {
      setSheetLoading(true);
      const { data } = await api.get("/ai-processes/sheets/config", {
        params: { processId },
      });
      setSheetSpreadsheetId(data.config?.spreadsheetId || "");
      setSheetName(data.config?.sheetName || "");
      setSheetStatus("");
    } catch (err) {
      toastError(err);
    } finally {
      setSheetLoading(false);
    }
  };

  useEffect(() => {
    loadConnections();
    loadSequences();
    Promise.all([fetchContactCustomFieldNames(), fetchWasheetMessageVariables()])
      .then(([contactFields, washeetFields]) => {
        setCustomFieldNames(contactFields || []);
        setWasheetVariables(washeetFields || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadProcesses();
  }, [connectionFilter]);

  useEffect(() => {
    if (tab === 1) loadRuns();
  }, [tab, processFilter]);

  const upsertSequence = sequence => {
    if (!sequence?.id) return;
    setSequences(prevSequences => {
      const exists = prevSequences.some(item => String(item.id) === String(sequence.id));
      return exists
        ? prevSequences.map(item => (String(item.id) === String(sequence.id) ? sequence : item))
        : [...prevSequences, sequence];
    });
  };

  const openCreateSequence = applySelection => {
    setSequenceModalId(null);
    sequenceModalApplySelection.current = applySelection;
    setSequenceModalOpen(true);
  };

  const openEditSequence = (sequenceId, applySelection) => {
    if (!sequenceId || sequenceId === NO_SEQUENCE) return;
    setSequenceModalId(sequenceId);
    sequenceModalApplySelection.current = applySelection;
    setSequenceModalOpen(true);
  };

  const handleSequenceSaved = savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    upsertSequence(savedSequence);
    if (savedSequenceId && sequenceModalApplySelection.current) {
      sequenceModalApplySelection.current(String(savedSequenceId));
    }
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    sequenceModalApplySelection.current = null;
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    sequenceModalApplySelection.current = null;
  };

  const updateStage = (stageIndex, values) => {
    setEditingProcess(prev => ({
      ...prev,
      stages: prev.stages.map((stage, index) =>
        index === stageIndex ? { ...stage, ...values } : stage
      ),
    }));
  };

  const updateStageItem = (stageIndex, itemType, itemIndex, values) => {
    setEditingProcess(prev => ({
      ...prev,
      stages: prev.stages.map((stage, index) => {
        if (index !== stageIndex) return stage;
        return {
          ...stage,
          [itemType]: (stage[itemType] || []).map((item, nextIndex) =>
            nextIndex === itemIndex ? { ...item, ...values } : item
          ),
        };
      }),
    }));
  };

  const removeStageItem = (stageIndex, itemType, itemIndex) => {
    setEditingProcess(prev => ({
      ...prev,
      stages: prev.stages.map((stage, index) => {
        if (index !== stageIndex) return stage;
        return {
          ...stage,
          [itemType]: (stage[itemType] || []).filter(
            (_, nextIndex) => nextIndex !== itemIndex
          ),
        };
      }),
    }));
  };

  const moveStage = (stageIndex, direction) => {
    setEditingProcess(prev => ({
      ...prev,
      stages: moveArrayItem(
        prev.stages || [],
        stageIndex,
        stageIndex + direction
      ),
    }));
  };

  const moveStageContentItem = (stageIndex, contentIndex, direction) => {
    setEditingProcess(prev => ({
      ...prev,
      stages: (prev.stages || []).map((stage, index) => {
        if (index !== stageIndex) return stage;
        const contentItems = moveArrayItem(
          getStageContentItems(stage),
          contentIndex,
          contentIndex + direction
        );
        const nextStage = {
          ...stage,
          fields: [...(stage.fields || [])],
          fileRequirements: [...(stage.fileRequirements || [])],
          messageBlocks: [...(stage.messageBlocks || [])],
        };

        contentItems.forEach((contentItem, position) => {
          nextStage[contentItem.type][contentItem.index] = {
            ...contentItem.item,
            position,
          };
        });

        return nextStage;
      }),
    }));
  };

  const moveFieldOption = (stageIndex, fieldIndex, optionIndex, direction) => {
    setEditingProcess(prev => ({
      ...prev,
      stages: (prev.stages || []).map((stage, index) => {
        if (index !== stageIndex) return stage;
        return {
          ...stage,
          fields: (stage.fields || []).map((field, nextFieldIndex) =>
            nextFieldIndex === fieldIndex
              ? {
                  ...field,
                  options: moveArrayItem(
                    field.options || [],
                    optionIndex,
                    optionIndex + direction
                  ),
                }
              : field
          ),
        };
      }),
    }));
  };

  const addVariableToMessageBlock = (stageIndex, blockIndex, variable) => {
    const block =
      (editingProcess.stages || [])[stageIndex]?.messageBlocks?.[blockIndex] || {};
    updateStageItem(stageIndex, "messageBlocks", blockIndex, {
      text: `${block.text || ""}${variable}`,
    });
  };

  const getConditionSourceFields = (targetStageIndex, targetFieldIndex = null) => {
    const sources = [];
    (editingProcess.stages || []).forEach((stage, stageIndex) => {
      const contentItems = getStageContentItems(stage);
      const foundTargetContentIndex = contentItems.findIndex(
        contentItem =>
          contentItem.type === "fields" && contentItem.index === targetFieldIndex
      );
      const targetContentIndex =
        targetFieldIndex === null || foundTargetContentIndex < 0
          ? null
          : foundTargetContentIndex;

      contentItems
        .map((contentItem, contentIndex) => ({ ...contentItem, contentIndex }))
        .filter(contentItem => contentItem.type === "fields")
        .forEach(contentItem => {
          const field = contentItem.item;
          const isBeforeStage = stageIndex < targetStageIndex;
          const isBeforeField =
            stageIndex === targetStageIndex &&
            (targetContentIndex === null || contentItem.contentIndex < targetContentIndex);
          const key = normalizeKey(field.key || field.label);

          if ((isBeforeStage || isBeforeField) && key) {
            sources.push({
              key,
              label: field.label || key,
              options: field.options || [],
            });
          }
        });
    });
    return sources;
  };

  const renderConditionalControls = ({
    stageIndex,
    itemType,
    itemIndex,
    item,
    sourceFields,
  }) => {
    const conditionalConfig = {
      ...emptyConditionalConfig(),
      ...(item.conditionalConfig || {}),
    };
    const selectedSource = sourceFields.find(
      field => field.key === conditionalConfig.sourceFieldKey
    );
    const sourceOptions = selectedSource?.options || [];

    return (
      <div className={classes.conditionalRow}>
        <div className={classes.conditionalHeader}>
          <Checkbox
            checked={conditionalConfig.enabled === true}
            onChange={event =>
              updateStageItem(stageIndex, itemType, itemIndex, {
                conditionalConfig: {
                  ...conditionalConfig,
                  enabled: event.target.checked,
                },
              })
            }
            color="primary"
          />
          <span>Campo condicional</span>
        </div>
        {conditionalConfig.enabled === true && (
          <div className={classes.conditionalFields}>
            <FormControl variant="outlined">
              <InputLabel>Mostrar cuando el campo</InputLabel>
              <Select
                label="Mostrar cuando el campo"
                value={conditionalConfig.sourceFieldKey || NO_FIELD_SELECTION}
                onChange={event =>
                  updateStageItem(stageIndex, itemType, itemIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      sourceFieldKey:
                        event.target.value === NO_FIELD_SELECTION
                          ? ""
                          : event.target.value,
                      value: "",
                    },
                  })
                }
              >
                <MenuItem value={NO_FIELD_SELECTION}>Seleccionar campo</MenuItem>
                {sourceFields.map(field => (
                  <MenuItem key={field.key} value={field.key}>
                    {field.label}
                  </MenuItem>
                ))}
                {!sourceFields.length && (
                  <MenuItem value="" disabled>
                    Sin campos anteriores
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel>Operador</InputLabel>
              <Select
                label="Operador"
                value={conditionalConfig.operator || "equals"}
                onChange={event =>
                  updateStageItem(stageIndex, itemType, itemIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      operator: event.target.value,
                    },
                  })
                }
              >
                {conditionOperators.map(operator => (
                  <MenuItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {sourceOptions.length ? (
              <FormControl variant="outlined">
                <InputLabel>Valor</InputLabel>
                <Select
                  label="Valor"
                  value={conditionalConfig.value || NO_FIELD_SELECTION}
                  onChange={event =>
                    updateStageItem(stageIndex, itemType, itemIndex, {
                      conditionalConfig: {
                        ...conditionalConfig,
                        value:
                          event.target.value === NO_FIELD_SELECTION
                            ? ""
                            : event.target.value,
                      },
                    })
                  }
                >
                  <MenuItem value={NO_FIELD_SELECTION}>Seleccionar valor</MenuItem>
                  {sourceOptions.map((option, optionIndex) => (
                    <MenuItem key={`${optionIndex}-${option}`} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                variant="outlined"
                label="Valor"
                value={conditionalConfig.value || ""}
                onChange={event =>
                  updateStageItem(stageIndex, itemType, itemIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      value: event.target.value,
                    },
                  })
                }
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const buildProcessPayload = (process, overrides = {}) => {
    const nextProcess = { ...process, ...overrides };
    const usedFieldKeys = new Set();
    const usedFileKeys = new Set();
    const sharedConnectionIds = normalizeConnectionIds(
      nextProcess.sharedConnectionIds,
      nextProcess.connectionId
    );
    const connectionId =
      nextProcess.connectionId === NO_CONNECTION
        ? sharedConnectionIds[0] || null
        : Number(nextProcess.connectionId);

    return {
      ...nextProcess,
      allowMultipleRuns: nextProcess.allowMultipleRuns === true,
      sendConfirmation: nextProcess.sendConfirmation !== false,
      finalSequenceId:
        nextProcess.finalSequenceId && nextProcess.finalSequenceId !== NO_SEQUENCE
          ? Number(nextProcess.finalSequenceId)
          : null,
      connectionId,
      sharedConnectionIds,
      stages: (nextProcess.stages || []).map((stage, stageIndex) => {
        const orderedStage = normalizeStageContentOrder(stage);
        return {
          ...orderedStage,
          position: stageIndex,
          fields: (orderedStage.fields || []).map((field, fieldIndex) => ({
            ...field,
            confirmValue: field.confirmValue !== false,
            sendPrompt: field.sendPrompt !== false,
            key: getUniqueKey(
              field.label || field.key,
              usedFieldKeys,
              `campo_${fieldIndex + 1}`
            ),
            options:
              field.type === "select" || field.type === "multiple"
                ? textToOptions(optionsToText(field.options))
                : [],
            conditionalConfig: normalizeConditionalPayload(field.conditionalConfig),
            position: Number(field.position || 0),
          })),
          fileRequirements: (orderedStage.fileRequirements || []).map(
            (file, fileIndex) => ({
              ...file,
              key: getUniqueKey(
                file.label || file.key,
                usedFileKeys,
                `archivo_${fileIndex + 1}`
              ),
              conditionalConfig: normalizeConditionalPayload(file.conditionalConfig),
              position: Number(file.position || 0),
            })
          ),
          messageBlocks: (orderedStage.messageBlocks || []).map(block => ({
            ...block,
            text: block.text || "",
            delaySeconds: Math.max(0, Number(block.delaySeconds || 0)),
            position: Number(block.position || 0),
          })),
        };
      }),
    };
  };

  const buildProcessFormData = payload => {
    const formData = new FormData();
    const stages = (payload.stages || []).map((stage, stageIndex) => ({
      ...stage,
      messageBlocks: (stage.messageBlocks || []).map((block, blockIndex) => {
        const uploadKey = `blockFile_${stageIndex}_${blockIndex}`;
        if (block.file) formData.append(uploadKey, block.file);

        return {
          ...block,
          uploadKey,
          file: undefined,
          media: block.file ? null : block.media,
        };
      }),
    }));

    Object.entries({ ...payload, stages }).forEach(([key, value]) => {
      if (key === "stages" || key === "sharedConnectionIds") {
        formData.append(key, JSON.stringify(value || []));
        return;
      }

      if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
        return;
      }

      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    return formData;
  };

  const processHasMessageFiles = payload =>
    (payload.stages || []).some(stage =>
      (stage.messageBlocks || []).some(block => Boolean(block.file))
    );

  const handleSave = async () => {
    try {
      const payload = buildProcessPayload(editingProcess);

      if (!payload.name.trim()) {
        toast.error("El proceso necesita un nombre.");
        return;
      }

      const requestData = processHasMessageFiles(payload)
        ? buildProcessFormData(payload)
        : payload;

      if (payload.id) {
        await api.put(`/ai-processes/${payload.id}`, requestData);
      } else {
        await api.post("/ai-processes", requestData);
      }

      toast.success("Proceso guardado.");
      setModalOpen(false);
      loadProcesses();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/ai-processes/${deleteTarget.id}`);
      toast.success("Proceso eliminado.");
      setDeleteTarget(null);
      loadProcesses();
    } catch (err) {
      toastError(err);
    }
  };

  const handleConfirmStatusToggle = async () => {
    if (!statusToggleProcess) return;
    const { process, nextActive } = statusToggleProcess;

    try {
      const payload = buildProcessPayload(normalizeProcess(process), {
        isActive: nextActive,
      });
      const { data } = await api.put(`/ai-processes/${process.id}`, payload);
      setProcesses(prev =>
        prev.map(item => (Number(item.id) === Number(data.id) ? data : item))
      );
      toast.success(nextActive ? "Proceso activado." : "Proceso desactivado.");
    } catch (err) {
      toastError(err);
    } finally {
      setStatusToggleProcess(null);
    }
  };

  const canResetTemplate = process =>
    Boolean(process?.id && DEFAULT_TEMPLATE_SLUGS.includes(process.slug));

  const handleResetTemplate = async () => {
    if (!resetTemplateTarget) return;
    try {
      const { data } = await api.post(
        `/ai-processes/${resetTemplateTarget.id}/reset-template`
      );
      toast.success("Plantilla restablecida.");
      setResetTemplateTarget(null);
      if (editingProcess.id === data.id) {
        setEditingProcess(normalizeProcess(data));
      }
      loadProcesses();
    } catch (err) {
      toastError(err);
    }
  };

  const selectedRunProcess = processes.find(
    process => String(process.id) === String(editingRun.processId)
  );

  const selectedRunFields = (selectedRunProcess?.stages || [])
    .reduce((items, stage) => [...items, ...(stage.fields || [])], [])
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));

  const selectedRunFileRequirements = (selectedRunProcess?.stages || [])
    .reduce((items, stage) => [...items, ...(stage.fileRequirements || [])], [])
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));

  const selectedRunFiles = (editingRun.files || []).reduce((acc, file) => {
    acc[file.fileKey] = file;
    return acc;
  }, {});

  const runToEditingRun = run => ({
    id: run.id,
    processId: run.process?.id || NO_PROCESS,
    status: run.status === "ready_to_finish" ? "in_progress" : run.status,
    conclusion: run.conclusion || "",
    contactId: run.contact?.id || run.contactId || "",
    ticketId: run.ticketId || "",
    values: (run.values || []).reduce((acc, value) => {
      acc[value.fieldId || value.fieldKey] = value.value || "";
      return acc;
    }, {}),
    files: run.files || [],
  });

  const openRunModal = run => {
    if (!run) {
      setEditingRun(emptyRun(processFilter !== ALL_PROCESSES ? processFilter : NO_PROCESS));
      setRunModalOpen(true);
      return;
    }

    setEditingRun(runToEditingRun(run));
    setRunModalOpen(true);
  };

  const openSheetModal = () => {
    const defaultProcessId =
      processFilter !== ALL_PROCESSES
        ? String(processFilter)
        : processes[0]?.id
        ? String(processes[0].id)
        : NO_PROCESS;

    setSheetProcessId(defaultProcessId);
    setSheetModalOpen(true);
    loadSheetConfig(defaultProcessId);
  };

  const saveSheetConfig = async (showToast = true) => {
    if (sheetProcessId === NO_PROCESS) {
      toast.error("Selecciona un proceso.");
      return null;
    }

    if (!sheetSpreadsheetId.trim()) {
      toast.error("Ingresa el ID del documento.");
      return null;
    }

    if (!sheetName.trim()) {
      toast.error("Ingresa el nombre de la hoja.");
      return null;
    }

    setSheetSaving(true);
    try {
      const { data } = await api.put("/ai-processes/sheets/config", {
        processId: Number(sheetProcessId),
        spreadsheetId: sheetSpreadsheetId.trim(),
        sheetName: sheetName.trim(),
      });
      if (showToast) toast.success("Configuracion guardada.");
      setSheetStatus("Configuracion guardada.");
      return data;
    } catch (err) {
      toastError(err);
      return null;
    } finally {
      setSheetSaving(false);
    }
  };

  const handleSyncSheetRuns = async () => {
    const savedConfig = await saveSheetConfig(false);
    if (!savedConfig) return;

    try {
      setSheetSyncing(true);
      const { data } = await api.post("/ai-processes/sheets/sync", {
        processId: Number(sheetProcessId),
      });
      toast.success("Registros enviados.");
      setSheetStatus(
        `${data.total || 0} registro(s) enviados en ${data.batches || 1} lote(s).`
      );
    } catch (err) {
      toastError(err);
    } finally {
      setSheetSyncing(false);
    }
  };

  const saveRun = async () => {
    if (editingRun.processId === NO_PROCESS) {
      toast.error("Selecciona un proceso.");
      return null;
    }

    const payload = {
      processId: Number(editingRun.processId),
      status: editingRun.status,
      conclusion: editingRun.conclusion || "",
      contactId: editingRun.contactId || null,
      ticketId: editingRun.ticketId || null,
      values: selectedRunFields.map(field => ({
        fieldId: field.id,
        fieldKey: field.key,
        value: editingRun.values[field.id] ?? editingRun.values[field.key] ?? "",
      })),
    };

    const { data } = editingRun.id
      ? await api.put(`/ai-processes/runs/${editingRun.id}`, payload)
      : await api.post("/ai-processes/runs", payload);

    setEditingRun(runToEditingRun(data));
    return data;
  };

  const handleSaveRun = async () => {
    try {
      const savedRun = await saveRun();
      if (!savedRun) return;
      toast.success("Registro guardado.");
      setRunModalOpen(false);
      loadRuns();
    } catch (err) {
      toastError(err);
    }
  };

  const handleUploadRunFile = async (fileKey, file) => {
    if (!file) return;

    try {
      setUploadingFileKey(fileKey);
      const run = editingRun.id ? editingRun : await saveRun();
      if (!run?.id) return;

      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(
        `/ai-processes/runs/${run.id}/files/${fileKey}`,
        formData
      );
      setEditingRun(runToEditingRun(data));
      toast.success("Archivo guardado.");
      loadRuns();
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingFileKey("");
    }
  };

  const handleRemoveRunFile = async () => {
    if (!editingRun.id || !deleteRunFileTarget?.fileKey) return;

    try {
      setUploadingFileKey(deleteRunFileTarget.fileKey);
      const { data } = await api.delete(
        `/ai-processes/runs/${editingRun.id}/files/${deleteRunFileTarget.fileKey}`
      );
      setEditingRun(prev => ({ ...prev, files: data.files || [] }));
      setDeleteRunFileTarget(null);
      toast.success("Archivo eliminado.");
      loadRuns();
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingFileKey("");
    }
  };

  const handleDeleteRun = async () => {
    if (!deleteRunTarget) return;

    try {
      await api.delete(`/ai-processes/runs/${deleteRunTarget.id}`);
      toast.success("Registro eliminado.");
      setDeleteRunTarget(null);
      loadRuns();
    } catch (err) {
      toastError(err);
    }
  };

  const getProcessConnectionLabel = process => {
    const connectionIds = normalizeConnectionIds(
      process.sharedConnectionIds,
      process.connectionId
    );
    if (!connectionIds.length) return "Sin conexion";
    return connectionIds.map(id => getConnectionName(connections, id)).join(", ");
  };

  const getFileUrl = file => {
    if (!file?.fileUrl) return "";
    if (/^https?:\/\//i.test(file.fileUrl)) return file.fileUrl;
    return `${(getBackendUrl() || "").replace(/\/+$/, "")}/public/${file.fileUrl.replace(/^\/?public\/?/, "")}`;
  };

  const isImageFile = file =>
    /^image\//i.test(file?.mimeType || "") ||
    /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(file?.fileName || file?.fileUrl || "");

  const isPdfFile = file =>
    /pdf/i.test(file?.mimeType || "") || /\.pdf$/i.test(file?.fileName || file?.fileUrl || "");

  const renderFilePreview = file => {
    const fileUrl = getFileUrl(file);
    if (!fileUrl) return null;

    if (isImageFile(file)) {
      return <ModalImageCors imageUrl={fileUrl} className={classes.filePreview} />;
    }

    if (isPdfFile(file)) {
      return (
        <iframe
          title={file.fileName || file.fileKey || "PDF"}
          className={classes.pdfPreview}
          src={fileUrl}
        />
      );
    }

    return null;
  };

  const editingProcessConnectionIds = normalizeConnectionIds(
    editingProcess.sharedConnectionIds,
    editingProcess.connectionId
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleteTarget ? `Eliminar ${deleteTarget.name}` : ""}
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      >
        Esta accion no elimina los chats, pero si quitara la plantilla del proceso.
      </ConfirmationModal>
      <ConfirmationModal
        title={deleteRunTarget ? `Eliminar ${deleteRunTarget.number}` : ""}
        open={Boolean(deleteRunTarget)}
        onClose={() => setDeleteRunTarget(null)}
        onConfirm={handleDeleteRun}
      >
        Esta accion eliminara el registro del proceso y sus datos guardados.
      </ConfirmationModal>
      <ConfirmationModal
        title={deleteRunFileTarget ? `Eliminar ${deleteRunFileTarget.label}` : ""}
        open={Boolean(deleteRunFileTarget)}
        onClose={() => setDeleteRunFileTarget(null)}
        onConfirm={handleRemoveRunFile}
      >
        Esta accion eliminara el archivo adjunto de este registro.
      </ConfirmationModal>
      <ConfirmationModal
        title={
          resetTemplateTarget
            ? `Restablecer ${resetTemplateTarget.name}`
            : "Restablecer plantilla"
        }
        open={Boolean(resetTemplateTarget)}
        onClose={() => setResetTemplateTarget(null)}
        onConfirm={handleResetTemplate}
      >
        Esta accion restaurara la plantilla base, incluyendo instrucciones, etapas,
        datos, documentos y opciones. La conexion configurada y el estado del proceso
        se mantienen.
      </ConfirmationModal>
      <ConfirmationModal
        title={
          statusToggleProcess?.nextActive
            ? "Activar proceso"
            : "Desactivar proceso"
        }
        open={Boolean(statusToggleProcess)}
        onClose={() => setStatusToggleProcess(null)}
        onConfirm={handleConfirmStatusToggle}
      >
        {statusToggleProcess?.nextActive
          ? "Este proceso volvera a estar disponible para la IA."
          : "Este proceso dejara de estar disponible para la IA hasta que lo actives nuevamente."}
      </ConfirmationModal>

      <MainHeader>
        <Title>Procesos IA</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => {
              setEditingProcess(emptyProcess());
              setModalOpen(true);
            }}
          >
            Nuevo proceso
          </Button>
          <Tooltip title="Configurar Google Sheets">
            <IconButton
              color="primary"
              size="small"
              onClick={openSheetModal}
              aria-label="Configurar Google Sheets"
            >
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs value={tab} onChange={(_, value) => setTab(value)} indicatorColor="primary" textColor="primary">
          <Tab label="Procesos" />
          <Tab label="Registros" />
        </Tabs>

        {tab === 0 && (
          <>
            <div className={classes.toolbar}>
              <Typography variant="body2" className={classes.muted}>
                Define etapas, datos requeridos y documentos que la IA debe completar.
              </Typography>
              <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                <InputLabel>Conexion</InputLabel>
                <Select
                  label="Conexion"
                  value={connectionFilter}
                  onChange={event => setConnectionFilter(event.target.value)}
                >
                  <MenuItem value={ALL_CONNECTIONS}>Todos</MenuItem>
                  {whatsappTabs
                    .filter(item => item.value !== "all")
                    .map(item => (
                      <MenuItem key={item.value} value={String(item.value)}>
                        {item.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>

            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Proceso</TableCell>
                  <TableCell>Conexion</TableCell>
                  <TableCell>Etapas</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processes.map(process => (
                  <TableRow key={process.id}>
                    <TableCell>
                      <Typography className={classes.processName}>{process.name}</Typography>
                      <Typography variant="caption" className={classes.muted}>
                        {process.description || process.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>{getProcessConnectionLabel(process)}</TableCell>
                    <TableCell>{process.stages?.length || 0}</TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={process.isActive !== false}
                        color="primary"
                        onChange={event =>
                          setStatusToggleProcess({
                            process,
                            nextActive: event.target.checked,
                          })
                        }
                        inputProps={{
                          "aria-label":
                            process.isActive !== false
                              ? "Desactivar proceso"
                              : "Activar proceso",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingProcess(normalizeProcess(process));
                            setModalOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {canResetTemplate(process) && (
                        <Tooltip title="Restablecer plantilla">
                          <IconButton
                            size="small"
                            onClick={() => setResetTemplateTarget(process)}
                          >
                            <Restore />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => setDeleteTarget(process)}>
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!processes.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={5}>No hay procesos configurados.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}

        {tab === 1 && (
          <>
            <div className={classes.toolbar}>
              <Typography variant="body2" className={classes.muted}>
                Registros creados por las tools de procesos durante la conversacion.
              </Typography>
              <div className={classes.filters}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 240 }}>
                  <InputLabel>Proceso</InputLabel>
                  <Select
                    label="Proceso"
                    value={processFilter}
                    onChange={event => setProcessFilter(event.target.value)}
                  >
                    <MenuItem value={ALL_PROCESSES}>Todos</MenuItem>
                    {processes.map(process => (
                      <MenuItem key={process.id} value={process.id}>
                        {process.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => openRunModal(null)}
                >
                  Nuevo registro
                </Button>
              </div>
            </div>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Registro</TableCell>
                  <TableCell>Proceso</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Faltantes</TableCell>
                  <TableCell>Archivos</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map(run => (
                  <TableRow key={run.id}>
                    <TableCell>{run.number}</TableCell>
                    <TableCell>{run.process?.name}</TableCell>
                    <TableCell>{run.contact?.name || run.customerName || "-"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={run.status} />
                    </TableCell>
                    <TableCell>
                      {[...(run.missingRequiredFields || []), ...(run.missingRequiredFiles || [])]
                        .map(item => item.label)
                        .join(", ") || "Completo"}
                    </TableCell>
                    <TableCell>
                      <div className={classes.chipWrap}>
                        {(run.files || []).map(file => (
                          <Chip
                            key={file.id || file.fileKey}
                            size="small"
                            icon={<InsertDriveFile />}
                            label={file.fileLabel || file.fileKey}
                            component="a"
                            clickable
                            href={getFileUrl(file)}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openRunModal(run)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => setDeleteRunTarget(run)}>
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!runs.length && (
                  <TableRow>
                    <TableCell colSpan={7}>No hay registros de procesos.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>

      <Dialog
        open={sheetModalOpen}
        onClose={() => setSheetModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Google Sheets</DialogTitle>
        <DialogContent dividers>
          <FormControl variant="outlined" margin="dense" fullWidth disabled={sheetLoading}>
            <InputLabel>Proceso</InputLabel>
            <Select
              label="Proceso"
              value={sheetProcessId || NO_PROCESS}
              onChange={event => {
                const nextProcessId = event.target.value;
                setSheetProcessId(nextProcessId);
                loadSheetConfig(nextProcessId);
              }}
            >
              <MenuItem value={NO_PROCESS}>Seleccionar proceso</MenuItem>
              {processes.map(process => (
                <MenuItem key={process.id} value={String(process.id)}>
                  {process.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className={classes.sheetConfigFields}>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label="ID del documento"
              value={sheetSpreadsheetId}
              disabled={sheetLoading}
              onChange={event => setSheetSpreadsheetId(event.target.value)}
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label="Hoja"
              value={sheetName}
              disabled={sheetLoading}
              onChange={event => setSheetName(event.target.value)}
            />
          </div>
          <Typography variant="body2" className={classes.sheetConfigStatus}>
            {sheetStatus}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSheetModalOpen(false)}>Cerrar</Button>
          <Button
            color="primary"
            variant="outlined"
            disabled={sheetLoading || sheetSaving || sheetSyncing}
            onClick={() => saveSheetConfig(true)}
          >
            {sheetSaving ? "Guardando..." : "Guardar"}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={sheetLoading || sheetSaving || sheetSyncing}
            onClick={handleSyncSheetRuns}
          >
            {sheetSyncing ? "Enviando..." : "Guardar y enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle className={classes.stickyDialogTitle}>
          <div className={classes.dialogTitleContent}>
            <span>{editingProcess.id ? "Editar proceso" : "Nuevo proceso"}</span>
            <div className={classes.dialogSwitches}>
              <FormControlLabel
                className={classes.headerSwitch}
                control={
                  <Switch
                    checked={editingProcess.allowMultipleRuns === true}
                    onChange={event =>
                      setEditingProcess(prev => ({
                        ...prev,
                        allowMultipleRuns: event.target.checked,
                      }))
                    }
                    color="primary"
                  />
                }
                label="Multiples registros"
              />
              <FormControlLabel
                className={classes.headerSwitch}
                control={
                  <Switch
                    checked={editingProcess.isActive !== false}
                    onChange={event =>
                      setEditingProcess(prev => ({
                        ...prev,
                        isActive: event.target.checked,
                      }))
                    }
                    color="primary"
                  />
                }
                label="Activo"
              />
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                variant="outlined"
                label="Nombre"
                value={editingProcess.name}
                onChange={event =>
                  setEditingProcess(prev => ({ ...prev, name: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Conexiones</InputLabel>
                <Select
                  multiple
                  label="Conexiones"
                  value={
                    editingProcessConnectionIds.length
                      ? editingProcessConnectionIds
                      : [NO_CONNECTION]
                  }
                  renderValue={selected =>
                    selected.includes(NO_CONNECTION)
                      ? "Sin conexion"
                      : selected.map(id => getConnectionName(connections, id)).join(", ")
                  }
                  onChange={event => {
                    const rawValue = event.target.value || [];
                    const selectedIds =
                      rawValue[rawValue.length - 1] === NO_CONNECTION
                        ? []
                        : normalizeConnectionIds(
                            rawValue.filter(value => value !== NO_CONNECTION)
                          );
                    setEditingProcess(prev => {
                      const previousConnectionId = Number(prev.connectionId);
                      const connectionId = selectedIds.includes(previousConnectionId)
                        ? previousConnectionId
                        : selectedIds[0] || NO_CONNECTION;

                      return {
                        ...prev,
                        connectionId,
                        sharedConnectionIds: selectedIds,
                      };
                    });
                  }}
                >
                  <MenuItem value={NO_CONNECTION}>
                    <Checkbox
                      checked={!editingProcessConnectionIds.length}
                    />
                    <ListItemText primary="Sin conexion" />
                  </MenuItem>
                  {connections.map(connection => (
                    <MenuItem key={connection.id} value={connection.id}>
                      <Checkbox
                        checked={editingProcessConnectionIds.includes(
                          Number(connection.id)
                        )}
                      />
                      <ListItemText
                        primary={
                          connection.name ||
                          connection.number ||
                          `Conexion ${connection.id}`
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Descripcion"
                value={editingProcess.description}
                onChange={event =>
                  setEditingProcess(prev => ({ ...prev, description: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                label="Instrucciones para la IA"
                value={editingProcess.instructions}
                onChange={event =>
                  setEditingProcess(prev => ({ ...prev, instructions: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                label="Como concluir"
                value={editingProcess.conclusionInstructions}
                onChange={event =>
                  setEditingProcess(prev => ({
                    ...prev,
                    conclusionInstructions: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingProcess.sendConfirmation !== false}
                    onChange={event =>
                      setEditingProcess(prev => ({
                        ...prev,
                        sendConfirmation: event.target.checked,
                      }))
                    }
                    color="primary"
                  />
                }
                label="Enviar confirmacion"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth margin="dense">
                <InputLabel>Secuencia final</InputLabel>
                <Select
                  label="Secuencia final"
                  value={editingProcess.finalSequenceId || NO_SEQUENCE}
                  onChange={event => {
                    if (event.target.value === "__create__") {
                      openCreateSequence(sequenceId =>
                        setEditingProcess(prev => ({
                          ...prev,
                          finalSequenceId: sequenceId,
                        }))
                      );
                      return;
                    }
                    setEditingProcess(prev => ({
                      ...prev,
                      finalSequenceId:
                        event.target.value === NO_SEQUENCE
                          ? NO_SEQUENCE
                          : event.target.value,
                    }));
                  }}
                >
                  <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                  <MenuItem value="__create__">Crear secuencia...</MenuItem>
                  {sequences
                    .filter(sequence => {
                      const sequenceConnectionId = Number(sequence.connectionId || 0);
                      return (
                        !editingProcessConnectionIds.length ||
                        editingProcessConnectionIds.includes(sequenceConnectionId) ||
                        String(sequence.id) === String(editingProcess.finalSequenceId || "")
                      );
                    })
                    .map(sequence => (
                      <MenuItem key={sequence.id} value={String(sequence.id)}>
                        {formatSequenceOption(sequence)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <div className={classes.sectionActions}>
                <Button
                  color="primary"
                  disabled={
                    !editingProcess.finalSequenceId ||
                    editingProcess.finalSequenceId === NO_SEQUENCE
                  }
                  onClick={() =>
                    openEditSequence(editingProcess.finalSequenceId, sequenceId =>
                      setEditingProcess(prev => ({
                        ...prev,
                        finalSequenceId: sequenceId,
                      }))
                    )
                  }
                >
                  Editar secuencia
                </Button>
              </div>
            </Grid>
          </Grid>

          {(editingProcess.stages || []).map((stage, stageIndex) => {
            const contentItems = getStageContentItems(stage);

            return (
              <div className={classes.stageBox} key={stage.id || stageIndex}>
                <div className={classes.stageHeader}>
                  <TextField
                    variant="outlined"
                    label={`Etapa ${stageIndex + 1}`}
                    value={stage.name}
                    onChange={event => updateStage(stageIndex, { name: event.target.value })}
                  />
                  <div className={classes.orderActions}>
                    <Tooltip title="Subir etapa">
                      <span>
                        <IconButton
                          disabled={stageIndex === 0}
                          onClick={() => moveStage(stageIndex, -1)}
                        >
                          <ArrowUpward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Bajar etapa">
                      <span>
                        <IconButton
                          disabled={stageIndex === (editingProcess.stages || []).length - 1}
                          onClick={() => moveStage(stageIndex, 1)}
                        >
                          <ArrowDownward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Eliminar etapa">
                      <IconButton
                        onClick={() =>
                          setEditingProcess(prev => ({
                            ...prev,
                            stages: prev.stages.filter((_, index) => index !== stageIndex),
                          }))
                        }
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  margin="dense"
                  variant="outlined"
                  label="Instrucciones de etapa"
                  value={stage.instructions || ""}
                  onChange={event =>
                    updateStage(stageIndex, { instructions: event.target.value })
                  }
                />

                <Typography variant="subtitle2">Contenido</Typography>
                {contentItems.map((contentItem, contentIndex) => {
                  const field = contentItem.type === "fields" ? contentItem.item : null;
                  const file =
                    contentItem.type === "fileRequirements" ? contentItem.item : null;
                  const block =
                    contentItem.type === "messageBlocks" ? contentItem.item : null;
                  const itemIndex = contentItem.index;
                  const itemTitle = field ? "Dato" : file ? "Documento" : "Mensaje";

                  return (
                    <div
                      className={classes.itemBox}
                      key={`${contentItem.type}-${contentItem.item.id || itemIndex}-${contentIndex}`}
                    >
                      <div className={classes.itemHeader}>
                        <Typography variant="subtitle2">
                          {contentIndex + 1}. {itemTitle}
                        </Typography>
                        <div className={classes.orderActions}>
                          <Tooltip title="Subir">
                            <span>
                              <IconButton
                                disabled={contentIndex === 0}
                                onClick={() =>
                                  moveStageContentItem(stageIndex, contentIndex, -1)
                                }
                              >
                                <ArrowUpward />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Bajar">
                            <span>
                              <IconButton
                                disabled={contentIndex === contentItems.length - 1}
                                onClick={() =>
                                  moveStageContentItem(stageIndex, contentIndex, 1)
                                }
                              >
                                <ArrowDownward />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              onClick={() =>
                                removeStageItem(stageIndex, contentItem.type, itemIndex)
                              }
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>

                      {field && (
                        <>
                          <div className={classes.rowFields}>
                            <TextField
                              variant="outlined"
                              label="Dato"
                              value={field.label}
                              onChange={event =>
                                updateStageItem(stageIndex, "fields", itemIndex, {
                                  label: event.target.value,
                                })
                              }
                            />
                            <FormControl variant="outlined">
                              <InputLabel>Tipo</InputLabel>
                              <Select
                                label="Tipo"
                                value={field.type || "text"}
                                onChange={event =>
                                  updateStageItem(stageIndex, "fields", itemIndex, {
                                    type: event.target.value,
                                    options:
                                      event.target.value === "select" ||
                                      event.target.value === "multiple"
                                        ? field.options || []
                                        : [],
                                  })
                                }
                              >
                                {fieldTypes.map(type => (
                                  <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.required !== false}
                                  onChange={event =>
                                    updateStageItem(stageIndex, "fields", itemIndex, {
                                      required: event.target.checked,
                                    })
                                  }
                                  color="primary"
                                />
                              }
                              label="Requerido"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.confirmValue !== false}
                                  onChange={event =>
                                    updateStageItem(stageIndex, "fields", itemIndex, {
                                      confirmValue: event.target.checked,
                                    })
                                  }
                                  color="primary"
                                />
                              }
                              label="Confirmar dato"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.sendPrompt !== false}
                                  onChange={event =>
                                    updateStageItem(stageIndex, "fields", itemIndex, {
                                      sendPrompt: event.target.checked,
                                    })
                                  }
                                  color="primary"
                                />
                              }
                              label="Enviar solicitud"
                            />
                          </div>
                          {renderConditionalControls({
                            stageIndex,
                            itemType: "fields",
                            itemIndex,
                            item: field,
                            sourceFields: getConditionSourceFields(stageIndex, itemIndex),
                          })}
                          {(field.type === "select" || field.type === "multiple") && (
                            <>
                              {(field.options || []).map((option, optionIndex) => (
                                <div
                                  className={classes.fieldOptionRow}
                                  key={`option-${stageIndex}-${itemIndex}-${optionIndex}`}
                                >
                                  <div className={classes.orderActions}>
                                    <Tooltip title="Subir opcion">
                                      <span>
                                        <IconButton
                                          disabled={optionIndex === 0}
                                          onClick={() =>
                                            moveFieldOption(
                                              stageIndex,
                                              itemIndex,
                                              optionIndex,
                                              -1
                                            )
                                          }
                                        >
                                          <ArrowUpward />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Bajar opcion">
                                      <span>
                                        <IconButton
                                          disabled={
                                            optionIndex === (field.options || []).length - 1
                                          }
                                          onClick={() =>
                                            moveFieldOption(
                                              stageIndex,
                                              itemIndex,
                                              optionIndex,
                                              1
                                            )
                                          }
                                        >
                                          <ArrowDownward />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </div>
                                  <TextField
                                    variant="outlined"
                                    label="ID"
                                    value={optionId(optionIndex)}
                                    disabled
                                  />
                                  <TextField
                                    variant="outlined"
                                    label="Opcion"
                                    value={option}
                                    onChange={event =>
                                      updateStageItem(stageIndex, "fields", itemIndex, {
                                        options: (field.options || []).map(
                                          (optionItem, index) =>
                                            index === optionIndex
                                              ? event.target.value
                                              : optionItem
                                        ),
                                      })
                                    }
                                  />
                                  <IconButton
                                    onClick={() =>
                                      updateStageItem(stageIndex, "fields", itemIndex, {
                                        options: (field.options || []).filter(
                                          (_, index) => index !== optionIndex
                                        ),
                                      })
                                    }
                                  >
                                    <DeleteOutline />
                                  </IconButton>
                                </div>
                              ))}
                              <div className={classes.sectionActions}>
                                <Button
                                  startIcon={<Add />}
                                  onClick={() =>
                                    updateStageItem(stageIndex, "fields", itemIndex, {
                                      options: [...(field.options || []), emptyOption()],
                                    })
                                  }
                                >
                                  Agregar opcion
                                </Button>
                              </div>
                            </>
                          )}
                        </>
                      )}

                      {file && (
                        <>
                          <div className={classes.fileRow}>
                            <TextField
                              variant="outlined"
                              label="Documento"
                              value={file.label}
                              onChange={event =>
                                updateStageItem(
                                  stageIndex,
                                  "fileRequirements",
                                  itemIndex,
                                  { label: event.target.value }
                                )
                              }
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={file.required !== false}
                                  onChange={event =>
                                    updateStageItem(
                                      stageIndex,
                                      "fileRequirements",
                                      itemIndex,
                                      { required: event.target.checked }
                                    )
                                  }
                                  color="primary"
                                />
                              }
                              label="Requerido"
                            />
                          </div>
                          {renderConditionalControls({
                            stageIndex,
                            itemType: "fileRequirements",
                            itemIndex,
                            item: file,
                            sourceFields: getConditionSourceFields(stageIndex, null),
                          })}
                        </>
                      )}

                      {block && (
                        <>
                          <div className={classes.messageRow}>
                            <MessageBubblePreview block={block} />
                            <TextField
                              fullWidth
                              multiline
                              minRows={5}
                              margin="dense"
                              variant="outlined"
                              label="Mensaje"
                              placeholder="Escribe el mensaje..."
                              value={block.text || ""}
                              onChange={event =>
                                updateStageItem(stageIndex, "messageBlocks", itemIndex, {
                                  text: event.target.value,
                                })
                              }
                            />
                            <TextField
                              className={classes.messageDelayField}
                              fullWidth
                              margin="dense"
                              variant="outlined"
                              label="Espera de"
                              type="number"
                              value={block.delaySeconds || 0}
                              inputProps={{ min: 0, step: 1 }}
                              helperText="Segundos"
                              onChange={event =>
                                updateStageItem(stageIndex, "messageBlocks", itemIndex, {
                                  delaySeconds: Math.max(0, Number(event.target.value)),
                                })
                              }
                            />
                          </div>

                          <div className={classes.variablesBox}>
                            <Typography
                              variant="caption"
                              className={classes.variablesLabel}
                            >
                              Variables disponibles
                            </Typography>
                            <div className={classes.variablesChips}>
                              {messageVariables.map(variable => (
                                <Chip
                                  key={`process-message-${stageIndex}-${itemIndex}-${variable.value}`}
                                  size="small"
                                  label={variable.label}
                                  onClick={() =>
                                    addVariableToMessageBlock(
                                      stageIndex,
                                      itemIndex,
                                      variable.value
                                    )
                                  }
                                />
                              ))}
                            </div>
                          </div>

                          <div className={classes.messageFileRow}>
                            <input
                              accept={acceptedMedia}
                              id={`process-message-file-${stageIndex}-${itemIndex}`}
                              type="file"
                              style={{ display: "none" }}
                              onChange={event =>
                                updateStageItem(
                                  stageIndex,
                                  "messageBlocks",
                                  itemIndex,
                                  {
                                    file: event.target.files?.[0] || null,
                                    media: event.target.files?.[0] ? null : block.media,
                                  }
                                )
                              }
                            />
                            <label htmlFor={`process-message-file-${stageIndex}-${itemIndex}`}>
                              <Button variant="outlined" component="span" size="small">
                                Seleccionar archivo
                              </Button>
                            </label>
                            <Typography
                              variant="body2"
                              className={classes.messageFileName}
                            >
                              {block.file?.name ||
                                block.media?.originalName ||
                                "Sin archivo"}
                            </Typography>
                            {(block.file || block.media) && (
                              <Button
                                size="small"
                                onClick={() =>
                                  updateStageItem(
                                    stageIndex,
                                    "messageBlocks",
                                    itemIndex,
                                    { file: null, media: null }
                                  )
                                }
                              >
                                Quitar
                              </Button>
                            )}
                          </div>
                          <small className={classes.helper}>
                            Imagenes, stickers .webp, audios, videos y documentos.
                          </small>
                        </>
                      )}
                    </div>
                  );
                })}

                <div className={classes.sectionActions}>
                  <Button
                    startIcon={<Add />}
                    onClick={() =>
                      updateStage(stageIndex, {
                        fields: [
                          ...(stage.fields || []),
                          { ...emptyField(), position: contentItems.length },
                        ],
                      })
                    }
                  >
                    Agregar dato
                  </Button>
                  <Button
                    startIcon={<InsertDriveFile />}
                    onClick={() =>
                      updateStage(stageIndex, {
                        fileRequirements: [
                          ...(stage.fileRequirements || []),
                          { ...emptyFile(), position: contentItems.length },
                        ],
                      })
                    }
                  >
                    Agregar documento
                  </Button>
                  <Button
                    startIcon={<Message />}
                    onClick={() =>
                      updateStage(stageIndex, {
                        messageBlocks: [
                          ...(stage.messageBlocks || []),
                          { ...emptyMessageBlock(), position: contentItems.length },
                        ],
                      })
                    }
                  >
                    Agregar mensaje
                  </Button>
                </div>
              </div>
            );
          })}
          <Button
            startIcon={<AssignmentTurnedIn />}
            onClick={() =>
              setEditingProcess(prev => ({
                ...prev,
                stages: [...(prev.stages || []), emptyStage()],
              }))
            }
          >
            Agregar etapa
          </Button>
        </DialogContent>
        <DialogActions>
          {canResetTemplate(editingProcess) && (
            <Button
              startIcon={<Restore />}
              onClick={() => setResetTemplateTarget(editingProcess)}
            >
              Restablecer
            </Button>
          )}
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleSave}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={runModalOpen} onClose={() => setRunModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRun.id ? "Editar registro" : "Nuevo registro"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth disabled={Boolean(editingRun.id)}>
                <InputLabel>Proceso</InputLabel>
                <Select
                  label="Proceso"
                  value={editingRun.processId || NO_PROCESS}
                  onChange={event =>
                    setEditingRun(prev => ({
                      ...prev,
                      processId: event.target.value,
                      values: {},
                    }))
                  }
                >
                  <MenuItem value={NO_PROCESS}>Seleccionar proceso</MenuItem>
                  {processes.map(process => (
                    <MenuItem key={process.id} value={process.id}>
                      {process.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={editingRun.status || "in_progress"}
                  onChange={event =>
                    setEditingRun(prev => ({ ...prev, status: event.target.value }))
                  }
                >
                  {runStatuses.map(status => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Ticket ID"
                value={editingRun.ticketId}
                onChange={event =>
                  setEditingRun(prev => ({ ...prev, ticketId: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Contacto ID"
                value={editingRun.contactId}
                onChange={event =>
                  setEditingRun(prev => ({ ...prev, contactId: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Conclusion"
                value={editingRun.conclusion}
                onChange={event =>
                  setEditingRun(prev => ({ ...prev, conclusion: event.target.value }))
                }
              />
            </Grid>
            {selectedRunFields.map(field => {
              const valueKey = field.id || field.key;

              return (
              <Grid item xs={12} md={field.type === "textarea" ? 12 : 6} key={valueKey}>
                {field.type === "select" && (field.options || []).length ? (
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>{`${field.label}${field.required ? " *" : ""}`}</InputLabel>
                    <Select
                      label={`${field.label}${field.required ? " *" : ""}`}
                      value={editingRun.values[valueKey] || NO_FIELD_SELECTION}
                      onChange={event =>
                        setEditingRun(prev => ({
                          ...prev,
                          values: {
                            ...prev.values,
                            [valueKey]:
                              event.target.value === NO_FIELD_SELECTION
                                ? ""
                                : event.target.value,
                          },
                        }))
                      }
                    >
                      <MenuItem value={NO_FIELD_SELECTION}>Sin seleccion</MenuItem>
                      {(field.options || []).map((option, optionIndex) => (
                        <MenuItem key={optionId(optionIndex)} value={optionId(optionIndex)}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : field.type === "multiple" && (field.options || []).length ? (
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>{`${field.label}${field.required ? " *" : ""}`}</InputLabel>
                    <Select
                      multiple
                      label={`${field.label}${field.required ? " *" : ""}`}
                      value={
                        valueToMultipleOptions(editingRun.values[valueKey]).length
                          ? valueToMultipleOptions(editingRun.values[valueKey])
                          : [NO_FIELD_SELECTION]
                      }
                      renderValue={selected =>
                        selectedOptionLabels(field.options || [], selected)
                      }
                      onChange={event => {
                        const selected = event.target.value.filter(
                          option => option !== NO_FIELD_SELECTION
                        );
                        setEditingRun(prev => ({
                          ...prev,
                          values: {
                            ...prev.values,
                            [valueKey]: selected.join(", "),
                          },
                        }));
                      }}
                    >
                      <MenuItem value={NO_FIELD_SELECTION}>Sin seleccion</MenuItem>
                      {(field.options || []).map((option, optionIndex) => (
                        <MenuItem key={optionId(optionIndex)} value={optionId(optionIndex)}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    multiline={field.type === "textarea"}
                    minRows={field.type === "textarea" ? 3 : undefined}
                    type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
                    InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
                    variant="outlined"
                    label={`${field.label}${field.required ? " *" : ""}`}
                    value={editingRun.values[valueKey] || ""}
                    onChange={event =>
                      setEditingRun(prev => ({
                        ...prev,
                        values: {
                          ...prev.values,
                          [valueKey]: event.target.value,
                        },
                      }))
                    }
                  />
                )}
              </Grid>
              );
            })}
            {editingRun.processId !== NO_PROCESS && !selectedRunFields.length && (
              <Grid item xs={12}>
                <Typography className={classes.muted}>
                  Este proceso no tiene campos de datos configurados.
                </Typography>
              </Grid>
            )}
            {selectedRunFileRequirements.length > 0 && (
              <Grid item xs={12} className={classes.runFilesSection}>
                <Typography variant="subtitle2">Archivos</Typography>
                {selectedRunFileRequirements.map(requirement => {
                  const currentFile = selectedRunFiles[requirement.key];
                  const fileUrl = getFileUrl(currentFile);

                  return (
                    <div key={requirement.key} className={classes.runFileCard}>
                      <div className={classes.runFileItem}>
                        <div>
                          <Typography variant="body2">
                            {requirement.label}
                            {requirement.required ? " *" : ""}
                          </Typography>
                          {currentFile ? (
                            <Typography variant="caption" className={classes.muted}>
                              {currentFile.fileName || currentFile.fileLabel}
                            </Typography>
                          ) : (
                            <Typography variant="caption" className={classes.muted}>
                              Sin archivo
                            </Typography>
                          )}
                        </div>
                        <div className={classes.runFileActions}>
                          <Button
                            component="label"
                            variant="outlined"
                            size="small"
                            disabled={uploadingFileKey === requirement.key}
                          >
                            {currentFile ? "Reemplazar" : "Subir"}
                            <input
                              type="file"
                              hidden
                              onChange={event => {
                                const file = event.target.files?.[0];
                                event.target.value = "";
                                handleUploadRunFile(requirement.key, file);
                              }}
                            />
                          </Button>
                          {currentFile && (
                            <Button
                              variant="outlined"
                              size="small"
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Ver
                            </Button>
                          )}
                          {currentFile && (
                            <Button
                              color="secondary"
                              variant="outlined"
                              size="small"
                              disabled={uploadingFileKey === requirement.key}
                              onClick={() =>
                                setDeleteRunFileTarget({
                                  fileKey: requirement.key,
                                  label: currentFile.fileName || requirement.label,
                                })
                              }
                            >
                              Eliminar
                            </Button>
                          )}
                        </div>
                      </div>
                      {currentFile ? (
                        <div className={classes.runFilePreviewWrap}>
                          {renderFilePreview(currentFile)}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunModalOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleSaveRun}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      {sequenceModalOpen && (
        <ChatbotModal
          open={sequenceModalOpen}
          onClose={handleSequenceModalClose}
          aria-labelledby="form-dialog-title"
          sequenceId={sequenceModalId}
          defaultConnectionId={editingProcessConnectionIds[0] || ""}
          onSave={handleSequenceSaved}
        />
      )}
    </MainContainer>
  );
};

export default Processes;
